import React, { useState, useEffect } from "react";
import api from "../../api/axios";
import BranchSelect from "../Branch";

function GroupForm({ group = null, onSuccess = () => {} }) {
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("");
  const [description, setDescription] = useState("");
  const [isCommonLedger, setIsCommonLedger] = useState("1"); // "1" => Yes, "0" => No
  const [branch, setBranch] = useState("");

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const token = localStorage.getItem("accessToken");

  // Fetch parent groups for the "Select Group / Head" dropdown
  useEffect(() => {
    const fetchGroups = async () => {      
      setLoadingGroups(true);
      try {
        const res = await api.get("/all-group-list?per_page=100",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ); // adjust endpoint if needed
        setGroups(res.data?.data || []);
      } catch (err) {
        console.error("Error fetching heads:", err);
        setError("Failed to load heads.");
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, []);

  // Sync local form state when the parent `group` prop changes (edit mode)
  useEffect(() => {
    if (group) {
      setName(group.group_name ?? group.group_name ?? "");
      setParentId(group.group_parent_id ? group.group_parent_id : "");
      setDescription(group.description ?? "");
      setIsCommonLedger(String(group.is_common ?? 1));
      setBranch(group.branch_id ? group.branch_id : "");
    } else {
      // reset to defaults for add mode
      setName("");
      setParentId("");
      setDescription("");
      setIsCommonLedger("1");
      setBranch("");
    }
    setError("");
    setSuccess("");
  }, [group]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!name.trim()) {
      setError("Head name is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        group_name: name.trim(),
        description: description || null,
        group_parent_id: parseInt(parentId) || null,
        is_common: parseInt(isCommonLedger), // 1 or 0
        branch_id: parseInt(branch) || null,
      };

      if (group && group.id) {
        await api.put(`/groups/${group.id}`, 
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSuccess("Head updated successfully.");
      } else {
        await api.post("/groups", 
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSuccess("Account Head created successfully.");
      }

      // let parent refresh list / clear selected group
      onSuccess && onSuccess();
    } catch (err) {
      console.error("Save error:", err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        "Failed to save head.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (group) {
      // restore to editing values
      setName(group.group_name ?? group.group_name ?? "");
      setParentId(group.group_parent_id ? group.group_parent_id : "");
      setDescription(group.description ?? "");
      setIsCommonLedger(String(group.is_common ?? 1));
      setBranch(group.branch_id ? group.branch_id : "");
    } else {
      // clear for add mode
      setName("");
      setParentId("");
      setDescription("");
      setIsCommonLedger("1");
      setBranch("");
    }
    setError("");
    setSuccess("");
  };

  return (
    <div>      

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSubmit} onReset={handleReset}>
        <div className="row">
          {/* Group name */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Head Name*</label>
            <input
              type="text"
              name="group_name"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          {/* Parent group / head */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Parent Head</label>
            <select
              name="group_parent_id"
              className="form-select"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="1">Choose...</option>
              {loadingGroups && <option disabled>Loading...</option>}
              {groups.map((g) => {
                // avoid allowing a group to be its own parent
                if (group && g.id === group.id) return null;
                const label = g.name ?? g.group_name ?? `#${g.id}`;
                return (
                  <option key={g.id} value={g.id}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>          

          {/* Description */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              className="form-control"
              rows="3"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Common Group */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Common Head ?</label>
            <select
             name="is_common"
              className="form-select"
              value={isCommonLedger}
              onChange={(e) => setIsCommonLedger(e.target.value)}
            >
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>
          </div>

          {/* Branch dropdown shown only when Common Group = No */}
          {isCommonLedger === "0" && (
            <div className="col-md-6 mb-3">
              <label className="form-label">Select Branch*</label>
              <BranchSelect value={branch} onChange={setBranch} />
            </div>
          )}

          <div className="col-12 d-flex justify-content-end gap-2 mt-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? (group ? "Updating..." : "Saving...") : group ? "Update" : "Save"}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleReset}
              disabled={saving}
            >
              Reset
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default GroupForm;
